/** @jsxImportSource @emotion/react */
"use client";

import type { Locale } from "@i18n/react";
import { useLocale, useTranslations } from "@i18n/react";
import { HallowABFlag, useMixpanel } from "@packages/ab";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useRequestGeolocation } from "@packages/sdk";

import { useTheme } from "../../../../global/emotion";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { useConfirm } from "../../_base/modals/Dialog/Dialog.emotion";

export const useUnderageErrorDialog = () => {
  const t = useTranslations();
  const { renderedTheme } = useTheme();
  const { flags } = useMixpanel();
  const analytics = useAnalytics();
  const isTargetedKidsProfile =
    flags?.[HallowABFlag.WebKidsProfileLaunch]?.isTargeted;
  const locale = useLocale();
  const { query: geolocationQuery } = useRequestGeolocation();

  return useConfirm({
    title: () => t("dob_ineligible_alert_title"),
    description: () => t("dob_ineligible_alert_message"),
    primaryButton: (onClick) => (
      <Button
        variant={renderedTheme === "dark" ? "darken" : "primary"}
        onClick={() => {
          analytics.track({
            event: HallowAnalyticsEvent.TappedModalPrimaryCTA,
            properties: {
              cta_lokalise_key: "general_word_okay",
              cta_text: t("general_word_okay"),
              modal_name: "under_13_cannot_create_account",
              screen_name: "birthdate",
            },
          });
          onClick();
        }}
        isFullWidth
      >
        {t("general_word_okay")}
      </Button>
    ),
    cancelButton:
      isTargetedKidsProfile &&
      ["en", "en-PH"].includes(locale as Locale) &&
      geolocationQuery.data?.country === "US"
        ? (onClick) => (
            <Button
              variant={renderedTheme === "dark" ? "darken" : "neutral"}
              onClick={() => {
                analytics.track({
                  event: HallowAnalyticsEvent.TappedModalSecondaryCTA,
                  properties: {
                    cta_lokalise_key: "dob_ineligible_learn_more",
                    cta_text: t("dob_ineligible_learn_more"),
                    modal_name: "under_13_cannot_create_account",
                    screen_name: "birthdate",
                  },
                });
                onClick();
              }}
              isFullWidth
              asChild
            >
              <a
                href="https://hallow.com/blog/introducing-kids-profiles-on-hallow/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("dob_ineligible_learn_more")}
              </a>
            </Button>
          )
        : undefined,
    buttonFormat: "vertical-reverse",
  });
};
